import { useState } from 'react';
import { ROICalculatorInput, ROICalculatorResult } from '../types';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

interface ROICalculatorProps {
  industryType: 'retail' | 'traffic' | 'enterprise';
  onClose: () => void;
}

const ROICalculator = ({ industryType, onClose }: ROICalculatorProps) => {
  const [inputs, setInputs] = useState<ROICalculatorInput>({});
  const [result, setResult] = useState<ROICalculatorResult | null>(null);

  const calculateROI = () => {
    let annualSavings = 0;
    let breakdown: { label: string; value: number }[] = [];

    if (industryType === 'retail') {
      const footfall = inputs.monthlyFootfall || 0;
      const avgTransaction = inputs.averageTransactionValue || 0;
      const conversionRate = inputs.currentConversionRate || 0;
      const locations = inputs.numberOfLocations || 1;

      const conversionImprovement = footfall * 0.15 * (avgTransaction * (conversionRate / 100));
      const operationalEfficiency = 50000 * locations;
      const shrinkageReduction = 30000 * locations;

      annualSavings = (conversionImprovement + operationalEfficiency + shrinkageReduction) * 12;
      
      breakdown = [
        { label: 'Conversion Improvement', value: conversionImprovement * 12 },
        { label: 'Operational Efficiency', value: operationalEfficiency * 12 },
        { label: 'Shrinkage Reduction', value: shrinkageReduction * 12 }
      ];
    } else if (industryType === 'traffic') {
      const intersections = inputs.numberOfIntersections || 0;
      const dailyTraffic = inputs.averageDailyTraffic || 0;

      const congestionReduction = intersections * 120000;
      const accidentPrevention = intersections * 80000;
      const emissionReduction = (dailyTraffic * intersections * 0.5);

      annualSavings = congestionReduction + accidentPrevention + emissionReduction;
      
      breakdown = [
        { label: 'Congestion Reduction', value: congestionReduction },
        { label: 'Accident Prevention', value: accidentPrevention },
        { label: 'Emission Reduction', value: emissionReduction }
      ];
    } else {
      const employees = inputs.numberOfEmployees || 0;
      const incidentCost = inputs.averageIncidentCost || 0;

      const securityImprovement = employees * 500;
      const incidentReduction = incidentCost * 0.6;
      const productivityGain = employees * 1200;

      annualSavings = securityImprovement + incidentReduction + productivityGain;
      
      breakdown = [
        { label: 'Security Improvement', value: securityImprovement },
        { label: 'Incident Reduction', value: incidentReduction },
        { label: 'Productivity Gain', value: productivityGain }
      ];
    }

    const implementationCost = 150000;
    const paybackPeriod = implementationCost / (annualSavings / 12);
    const roi = ((annualSavings - implementationCost) / implementationCost) * 100;

    setResult({
      annualSavings,
      paybackPeriod,
      roi,
      breakdown
    });
  };

  const handleInputChange = (field: keyof ROICalculatorInput, value: string) => {
    setInputs(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const renderInputFields = () => {
    if (industryType === 'retail') {
      return (
        <>
          <Input
            label="Monthly Footfall"
            type="number"
            placeholder="e.g., 50000"
            value={inputs.monthlyFootfall || ''}
            onChange={(e) => handleInputChange('monthlyFootfall', e.target.value)}
          />
          <Input
            label="Average Transaction Value ($)"
            type="number"
            placeholder="e.g., 75"
            value={inputs.averageTransactionValue || ''}
            onChange={(e) => handleInputChange('averageTransactionValue', e.target.value)}
          />
          <Input
            label="Current Conversion Rate (%)"
            type="number"
            placeholder="e.g., 3.5"
            value={inputs.currentConversionRate || ''}
            onChange={(e) => handleInputChange('currentConversionRate', e.target.value)}
          />
          <Input
            label="Number of Locations"
            type="number"
            placeholder="e.g., 5"
            value={inputs.numberOfLocations || ''}
            onChange={(e) => handleInputChange('numberOfLocations', e.target.value)}
          />
        </>
      );
    } else if (industryType === 'traffic') {
      return (
        <>
          <Input
            label="Number of Intersections"
            type="number"
            placeholder="e.g., 20"
            value={inputs.numberOfIntersections || ''}
            onChange={(e) => handleInputChange('numberOfIntersections', e.target.value)}
          />
          <Input
            label="Average Daily Traffic"
            type="number"
            placeholder="e.g., 15000"
            value={inputs.averageDailyTraffic || ''}
            onChange={(e) => handleInputChange('averageDailyTraffic', e.target.value)}
          />
        </>
      );
    } else {
      return (
        <>
          <Input
            label="Number of Employees"
            type="number"
            placeholder="e.g., 500"
            value={inputs.numberOfEmployees || ''}
            onChange={(e) => handleInputChange('numberOfEmployees', e.target.value)}
          />
          <Input
            label="Average Incident Cost ($)"
            type="number"
            placeholder="e.g., 25000"
            value={inputs.averageIncidentCost || ''}
            onChange={(e) => handleInputChange('averageIncidentCost', e.target.value)}
          />
        </>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-brand-lg">
        <div className="sticky top-0 bg-background border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">ROI Calculator</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors duration-300"
          >
            <Icon name="X" size={24} className="text-foreground" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {renderInputFields()}
          </div>
          
          <Button
            variant="default"
            size="lg"
            fullWidth
            iconName="Calculator"
            iconPosition="left"
            onClick={calculateROI}
          >
            Calculate ROI
          </Button>
          
          {result && (
            <div className="mt-8 space-y-6">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="bg-primary/5 rounded-xl p-6 border border-primary/20">
                  <div className="text-sm text-text-secondary mb-2">Annual Savings</div>
                  <div className="text-3xl font-bold text-primary">
                    ${result.annualSavings.toLocaleString()}
                  </div>
                </div>
                
                <div className="bg-success/5 rounded-xl p-6 border border-success/20">
                  <div className="text-sm text-text-secondary mb-2">Payback Period</div>
                  <div className="text-3xl font-bold text-success">
                    {result.paybackPeriod.toFixed(1)} months
                  </div>
                </div>
                
                <div className="bg-accent/5 rounded-xl p-6 border border-accent/20">
                  <div className="text-sm text-text-secondary mb-2">ROI</div>
                  <div className="text-3xl font-bold text-accent">
                    {result.roi.toFixed(0)}%
                  </div>
                </div>
              </div>
              
              <div className="bg-card rounded-xl p-6 border border-border">
                <h3 className="text-lg font-bold text-foreground mb-4">Savings Breakdown</h3>
                <div className="space-y-3">
                  {result.breakdown.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-foreground">{item.label}</span>
                      <span className="font-semibold text-primary">
                        ${item.value.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ROICalculator;